import { useT } from "../../i18n/index";
import { Icon } from "../../ui/icons";
import { SUITABILITY_COMPONENTS, type RecommendationOffer } from "./types";

// The Suitability ranking — the differentiator made visible (ADR 0009). Every
// gathered offer, ranked by its server-computed Suitability, with the four
// components, the citations (quality snapshot + equivalence version it cited),
// the must-have hard-gate where it caps an offer, and the suggestion. The
// advisor DECIDES — suggest-not-decide: the suggestion is a badge, the choice is
// an explicit click (handled by the parent).
export function SuitabilityRanking({
  offers,
  onChoose,
}: {
  offers: RecommendationOffer[];
  onChoose?: (offer: RecommendationOffer) => void;
}) {
  const { t } = useT();
  const ranked = [...offers].sort((a, b) => a.rank - b.rank);
  return (
    <ol className="rec-ranking" aria-label={t("rec.suitability")}>
      {ranked.map((o) => {
        const gated = (o.mustHavesUnmet?.length ?? 0) > 0;
        return (
          <li
            key={o.id}
            className="rec-offer k-card k-card--pad-sm"
            data-testid="rec-offer"
            data-offer={o.offerId}
            style={
              o.wasSuggested
                ? { borderColor: "var(--k-blue)", boxShadow: "0 0 0 1px var(--k-blue)" }
                : undefined
            }
          >
            {(o.wasSuggested || o.wasChosen || gated) && (
              <div className="rec-offer-tags" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {o.wasSuggested && (
                  <span className="k-badge k-badge--prive">
                    <Icon name="suggestion" size={14} />
                    {t("rec.suggestion")}
                  </span>
                )}
                {o.wasChosen && (
                  <span className="k-badge k-badge--success">
                    <Icon name="check" size={14} />
                    {t("rec.chosen")}
                  </span>
                )}
                {gated && (
                  <span className="k-badge k-badge--priority" data-testid="hard-gate">
                    <Icon name="alert-triangle" size={14} />
                    {t("rec.hardGate")}: {o.mustHavesUnmet!.join(", ")}
                  </span>
                )}
              </div>
            )}

            <div className="rec-offer-head" style={{ marginTop: 12 }}>
              <span className="k-chip rec-rank">#{o.rank}</span>
              <span className="rec-carrier" style={{ fontWeight: 600, color: "var(--k-ink)" }}>
                {o.carrierId}
              </span>
              <span className="rec-suitability k-chip" style={{ marginLeft: "auto" }}>
                <Icon name="scorecard" size={15} />
                {t("rec.suitability")}: <strong>{o.suitability}</strong>
              </span>
            </div>

            <div
              className="rec-offer-price"
              style={{ margin: "10px 0", display: "flex", alignItems: "baseline", gap: 6 }}
            >
              <span className="k-stat__value" aria-label={t("rec.premium")}>
                {o.grossPremium}
              </span>
              <span className="rec-offer-cur" style={{ color: "var(--k-muted)", fontWeight: 600 }}>
                {o.currency}
              </span>
            </div>

            <div className="rec-components">
              {SUITABILITY_COMPONENTS.map((c) => (
                <span key={c.key} className="rec-component k-chip">
                  {t(c.labelKey)}: <strong>{o[c.key]}</strong>
                </span>
              ))}
            </div>

            <div className="rec-offer-foot muted" style={{ marginTop: 12 }}>
              <span className="rec-citation">
                {t("rec.citation")}: {t("rec.qualitySnapshot")}{" "}
                {o.carrierQualitySnapshotId.slice(0, 8)} · {t("rec.equivVersion")}{" "}
                {o.equivalenceVersion}
              </span>
              {onChoose && (
                <button
                  type="button"
                  className="k-btn k-btn--secondary k-btn--sm"
                  onClick={() => onChoose(o)}
                >
                  {t("rec.choose")}
                  <Icon name="arrow-right" size={15} />
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
